import { Dispatch, SetStateAction, memo } from 'react';
import { Grid } from '@mantine/core';
import { type MRT_ColumnDef, type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import Table from '../../../components/Table';
import { formatMoneyString } from '../../../utils/strings';
import { DiscountCodesTableInfoData } from '.';
import FoliosCell from './FoliosCell';

export default memo(function TableView({
  tableInfoData = [],
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination
}: {
  tableInfoData: DiscountCodesTableInfoData[];
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName', 'DiscountsPage', 'ReportsPage']);
  const columns: MRT_ColumnDef<DiscountCodesTableInfoData>[] = [
    {
      accessorFn: (row) => row.definitionMissing
        ? t('ReportsPage:discountCodesReport.deletedDiscount')
        : row.name,
      header: t('FieldName:name')
    },
    {
      accessorFn: (row) => row.code || '—',
      header: t('FieldName:code')
    },
    {
      accessorFn: (row) =>
        t(`ReportsPage:discountCodesReport.sources.${row.source || 'code'}`),
      header: t('ReportsPage:discountCodesReport.source')
    },
    {
      accessorFn: (row) => row.type === 'unknown'
        ? t('ReportsPage:discountCodesReport.unknownDiscountType')
        : t(`DiscountsPage:discountCodes.typeOptions.${row.type}`),
      header: t('ReportsPage:discountCodesReport.discountType')
    },
    {
      accessorFn: (row) => row.usedCounter,
      header: t('ReportsPage:discountCodesReport.applications')
    },
    {
      accessorFn: (row) => {
        const formattedAmount = formatMoneyString(row.totalDiscounted || 0);

        if (row.amountComplete !== false) return formattedAmount;

        return t('ReportsPage:discountCodesReport.partialAmount', {
          amount: formattedAmount,
          count: row.applicationsWithoutAmount || 0
        });
      },
      header: t('ReportsPage:discountCodesReport.totalDiscounted')
    },
    {
      id: 'folios',
      header: t('ReportsPage:discountCodesReport.folios'),
      Cell: ({ row }) => <FoliosCell orders={row.original.orders || []} />
    }
  ];

  return (
    <>
      <Grid gutter="md">
        <Grid.Col span={12}>
          <Table
            tableColumns={columns}
            pagination={pagination}
            setPagination={setPagination}
            tableData={tableInfoData}
            isError={isError}
            paginationIsLoading={isLoading}
            numPages={pages}
            totalRowsCount={totalRowsCount}
            overrideTableConfigs={{
              manualPagination: false
            }}
          />
        </Grid.Col>
      </Grid>
    </>
  );
});
